'use client';
import { useEffect, useState } from 'react';

const DISMISS_KEY = 'pwa_install_dismissed';
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already running as a standalone PWA.
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Don't show if dismissed recently.
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (ts && Date.now() - Number(ts) < DISMISS_DURATION_MS) return;
    } catch {
      // localStorage may be blocked in some contexts; proceed.
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="banner"
      aria-label="Install app banner"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 900,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'var(--surface-1, #1c1c1e)',
        border: '1px solid var(--border, rgba(255,255,255,0.1))',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        maxWidth: 'min(520px, calc(100vw - 32px))',
        width: '100%',
      }}
    >
      <span
        className="material-symbols-outlined"
        style={{ fontSize: 24, color: 'var(--accent, #8b7aff)', flexShrink: 0 }}
        aria-hidden="true"
      >
        install_mobile
      </span>

      <p style={{ flex: 1, margin: 0, fontSize: 14, lineHeight: 1.4, color: 'var(--ink-1, #e8e8e8)' }}>
        Install <strong>Prompt Save</strong> as an app — faster &amp; works offline.
      </p>

      <button
        onClick={handleInstall}
        className="ps-btn ps-btn-primary"
        style={{ flexShrink: 0, fontSize: 13, padding: '6px 14px' }}
      >
        Install
      </button>

      <button
        onClick={handleDismiss}
        className="ps-btn-icon"
        aria-label="Dismiss install banner"
        style={{ flexShrink: 0 }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
      </button>
    </div>
  );
}
